import { useMutation, useQuery } from '@tanstack/react-query';
import moment from 'moment';
import { useSnackbar } from 'notistack';

import { useTranslation } from 'react-i18next';
import { getTimezone, isMobileDevice } from '@mgm/common/utils';
import { usePostHog } from 'posthog-js/react';
import { useAppDispatch, useAppSelector } from '../useAppStore';
import { getAuthenticateUser } from '../../store/authenticate/selectors';
import { ClockController } from '../../services';
import {
  IBaseResponse, TAxiosError,
  TClockStateRes,
  TShopStatusRes,
  TStartBreakRes,
} from '../../types/responses';
import { clockActions } from '../../store/clock/slice';
import { getBreakIndex } from '../../store/clock/selectors';
import { useGetSheetsAPI } from './useSheets';
import { getMembers } from '../../store/team/selectors';
import { TLocation, TPersonnelStatus } from '../../types';
import { QUERY_KEYS } from '../../utils/enums';
import { useError } from '../useCommon';

export function useShopStatusAPI() {
  const { personnel } = useAppSelector(getAuthenticateUser);
  const members = useAppSelector(getMembers);
  const dispatch = useAppDispatch();
  const { handleReqError } = useError();

  const getShopStatusApi = () => {
    const data = {
      shopName: personnel.shopName,
    };

    return ClockController().shopStatus<TShopStatusRes>(data);
  };

  return useMutation([QUERY_KEYS.SHOP_STATE], getShopStatusApi, {
    onSuccess: (rsp, variables) => {
      if (rsp?.success && 'personnelStatus' in rsp) {
        const memberToStatusMap = members.reduce((acc, member) => {
          const currentMember = rsp.personnelStatus.find((status) => status.email === member.email) || {} as TPersonnelStatus;

          if (currentMember?.email) acc[member.email] = { ...currentMember, avatar: member.avatarFile };
          return acc;
        }, {} as { [key: string]: TPersonnelStatus & { avatar: string | null } });

        dispatch(clockActions.setPersonnelStatus(memberToStatusMap));
      } else if (rsp?.error && rsp.error?.response?.status) {
        handleReqError(rsp.error, variables);
      } else if ('msg' in rsp) {
        console.error(`ERROR! shop status failed! ${rsp.msg}`);
      }
    },
    onError: (error) => {
      console.error(`ERROR! shop status request threw an Exception! ${error}`);
    },
  });
}

export function useClockStateAPI() {
  const { personnel } = useAppSelector(getAuthenticateUser);
  const dispatch = useAppDispatch();
  const { handleReqError } = useError();

  return useQuery(
    [QUERY_KEYS.CLOCK_STATE],
    async () => {
      const data = {
        email: personnel.email,
        date: moment().format(),
        shopName: personnel.shopName,
        timezone: getTimezone(),
      };

      const rsp = await ClockController().getClockState<TClockStateRes>(data);

      if (rsp?.success && 'started' in rsp) {
        dispatch(clockActions.setShiftDetails(rsp));

        dispatch(clockActions.setIsShiftStarted(rsp.started));
      } else if (rsp?.error && rsp.error?.response?.status) {
        handleReqError(rsp.error);
      } else if ('msg' in rsp) {
        console.error(`ERROR! get state failed! ${rsp.msg}`);
      }

      return rsp;
    },
    {
      enabled: false,
    },
  );
}

export function useStartShiftAPI() {
  const { enqueueSnackbar } = useSnackbar();
  const { personnel } = useAppSelector(getAuthenticateUser);
  const dispatch = useAppDispatch();
  const { t } = useTranslation('clock');
  const { handleReqError } = useError();
  const { mutateAsync: shopStatusMutate } = useShopStatusAPI();
  const posthog = usePostHog();

  const startShiftApi = (payload: { location: TLocation }) => {
    const data = {
      timezone: getTimezone(),
      email: personnel.email,
      shopName: personnel.shopName,
      location: payload.location,
      clockInVia: isMobileDevice ? 'Mobile' : 'Web',
    };

    return ClockController().startShift<IBaseResponse>(data);
  };

  return useMutation(startShiftApi, {
    onSuccess: async (rsp) => {
      posthog.capture('ManageMate_web', {
        shopName: personnel?.shopName,
        email: personnel?.email,
        addedAt: new Date().toISOString(),
        action: 'user_clocked_in',
      });

      if (rsp.success) {
        await shopStatusMutate();
        dispatch(clockActions.setIsShiftStarted(true));
        enqueueSnackbar(`${t('youAreClockedIn')}`, { variant: 'success' });
      } else if (rsp?.error && rsp.error?.response?.status) {
        handleReqError(rsp.error);
      } else if ('msg' in rsp) {
        console.error(`ERROR! start shift failed! ${rsp.msg}`);
      }
    },
    onError: (error: TAxiosError) => {
      console.error(`ERROR! start shift request threw an Exception! ${error}`);
    },
  });
}

export function useEndShiftAPI() {
  const { refetch: refetchSheets } = useGetSheetsAPI();
  const { enqueueSnackbar } = useSnackbar();
  const { personnel } = useAppSelector(getAuthenticateUser);
  const dispatch = useAppDispatch();
  const { handleReqError } = useError();
  const { t } = useTranslation('clock');
  const { mutateAsync: shopStatusMutate } = useShopStatusAPI();
  const posthog = usePostHog();

  const endShiftApi = () => {
    const data = {
      email: personnel.email,
      shopName: personnel.shopName,
    };

    return ClockController().endShift<IBaseResponse>(data);
  };

  return useMutation(endShiftApi, {
    onSuccess: async (rsp, variables) => {
      posthog.capture('ManageMate_web', {
        shopName: personnel?.shopName,
        email: personnel?.email,
        addedAt: new Date().toISOString(),
        action: 'user_clocked_out',
      });

      if (rsp.success) {
        await shopStatusMutate();
        dispatch(clockActions.setIsShiftStarted(false));
        enqueueSnackbar(`${t('yourClockoutHasBeenConfirm')}`, { variant: 'success' });
        await refetchSheets();
      } else if (rsp?.error && rsp.error?.response?.status) {
        handleReqError(rsp.error, variables);
      } else if ('msg' in rsp) {
        console.error(`ERROR! end shift failed! ${rsp.msg}`);
      }
    },
    onError: (error: TAxiosError) => {
      console.error(`ERROR! end shift request threw an Exception! ${error}`);
    },
  });
}

export function useStartBreakAPI() {
  const { enqueueSnackbar } = useSnackbar();
  const { personnel } = useAppSelector(getAuthenticateUser);
  const dispatch = useAppDispatch();
  const { t } = useTranslation('clock');
  const { handleReqError } = useError();
  const posthog = usePostHog();

  const startBreakApi = () => {
    const data = {
      shopName: personnel.shopName,
      timezone: getTimezone(),
      email: personnel.email,
    };

    return ClockController().startBreak<TStartBreakRes>(data);
  };

  return useMutation(startBreakApi, {
    onSuccess: async (rsp, variables) => {
      posthog.capture('ManageMate_web', {
        shopName: personnel?.shopName,
        email: personnel?.email,
        addedAt: new Date().toISOString(),
        action: 'ManageMate_web',
      });

      if (rsp.success && 'breakIndex' in rsp) {
        dispatch(clockActions.setBreakIndex(rsp.breakIndex));
        dispatch(clockActions.setIsBreakStarted(true));
        enqueueSnackbar(`${t('yourBreakHasBeenConfirm')}`, { variant: 'success' });
      } else if (rsp?.error && rsp.error?.response?.status) {
        handleReqError(rsp.error, variables);
      } else if ('msg' in rsp) {
        console.error(`ERROR! start break failed! ${rsp.msg}`);
      }
    },
    onError: (error: TAxiosError) => {
      console.error(`ERROR! start break request threw an Exception! ${error}`);
    },
  });
}

export function useEndBreakAPI() {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation('clock');
  const { personnel } = useAppSelector(getAuthenticateUser);
  const breakIndex = useAppSelector(getBreakIndex);
  const dispatch = useAppDispatch();
  const { handleReqError } = useError();
  const posthog = usePostHog();

  const endBreakApi = () => {
    const data = {
      breakIndex,
      shopName: personnel.shopName,
      timezone: getTimezone(),
      email: personnel.email,
    };

    return ClockController().endBreak<IBaseResponse>(data);
  };

  return useMutation(endBreakApi, {
    onSuccess: async (rsp, variables) => {
      posthog.capture('ManageMate_web', {
        shopName: personnel?.shopName,
        email: personnel?.email,
        addedAt: new Date().toISOString(),
        action: 'user_end_break',
      });

      if (rsp?.success) {
        dispatch(clockActions.setIsBreakStarted(false));
        enqueueSnackbar(`${t('enjoyYourShift')}`, { variant: 'success' });
      } else if (rsp?.error && rsp.error?.response?.status) {
        handleReqError(rsp.error, variables);
      } else if ('msg' in rsp) {
        console.error(`ERROR! end break failed! ${rsp.msg}`);
      }
    },
    onError: (error: TAxiosError) => {
      console.error(`ERROR! end break request threw an Exception! ${error}`);
    },
  });
}

export function useShopStatusQueryAPI() {
  const { personnel } = useAppSelector(getAuthenticateUser);
  const members = useAppSelector(getMembers);
  const dispatch = useAppDispatch();
  const { handleReqError } = useError();

  return useQuery(['shopStatus', personnel.shopName], async () => {
    const data = {
      shopName: personnel.shopName,
    };

    const rsp = await ClockController().shopStatus<TShopStatusRes>(data);

    if (rsp?.success && 'personnelStatus' in rsp) {
      const memberToStatusMap = members.reduce((acc, member) => {
        const currentMember = rsp.personnelStatus?.find((status) => status.email === member.email) || {} as TPersonnelStatus;

        if (currentMember?.email) acc[member.email] = { ...currentMember, avatar: member.avatarFile };
        return acc;
      }, {} as { [key: string]: TPersonnelStatus & { avatar: string | null } });

      dispatch(clockActions.setPersonnelStatus(memberToStatusMap));
    } else if (rsp?.error && rsp.error?.response?.status) {
      handleReqError(rsp.error, personnel.shopName);
    } else if ('msg' in rsp) {
      console.error(`ERROR! shop status failed! ${rsp.msg}`);
    }

    return rsp;
  });
}
